
import supportPostQuery from "~/apollo/queries/support/post";
import lenisScrollMixin from "@/mixins/lenisScrollMixin";
import previewModeMixin from "@/mixins/previewModeMixin";
import fetchPostMixin from "@/mixins/fetchPostMixin";
import seoMixin from "@/mixins/seoMixin";
import { throttle } from "throttle-debounce";
import { retryApolloQuery } from "~/utils/retryApolloQuery";

export default {
  name: 'SupportPostSlug',
  layout: 'support',
  colorMode: 'light',
  mixins: [lenisScrollMixin, previewModeMixin, fetchPostMixin, seoMixin],
  async asyncData({ app, route, store }) {
    const previewMode = store.state.previewMode;

    if (previewMode) {
      await app.apolloProvider.defaultClient.resetStore();
    }

    try {
      // Fetch supportPost data with retry mechanism
      const supportPostResponse = await retryApolloQuery(
        app.apolloProvider.defaultClient,
        {
          query: supportPostQuery,
          variables: {
            slug: route.params.slug,
            locale: app.i18n?.locale || 'en',
          },
        },
        {
          maxAttempts: 5,
          onRetry: (error, attempt, delay) => {
            console.log(`Support post query retry ${attempt}/5 for slug: ${route.params.slug}`)
          },
          fallbackData: { data: { supportPosts: { data: [] } } }
        }
      );

      // Check if we have valid data
      if (!supportPostResponse?.data?.supportPosts?.data?.length) {
        console.warn(`No support post found for slug: ${route.params.slug}`);
        return { supportPost: null, previewMode };
      }

      const supportPost = supportPostResponse.data.supportPosts.data[0];

      // Set route parameters for localization
      app.store.dispatch(
        "i18n/setRouteParams",
        Object.fromEntries(
          supportPost.attributes.localizations.data.map((el) => [
            el.attributes.locale,
            { slug: el.attributes.slug },
          ])
        )
      );

      return {
        supportPost,
        previewMode,
      };
    } catch (error) {
      console.error('Error in support post asyncData:', error);
      return { supportPost: null, previewMode };
    }
  },
  data() {
    return {
      supportPost: null,
      activeSection: '',
    };
  },
  computed: {
    tocSections() {
      if (!this.supportPost) return [];
      const sections = this.supportPost.attributes?.sections || [];
      // Check if at least one section has a non-empty title.
      const hasTitledSection = sections.some(
        (section) => section.title && section.title.trim() !== ''
      );
      if (hasTitledSection) {
        // Map over sections with titles and include the generated id.
        return sections
          .map((section, index) => {
            if (section.title && section.title.trim() !== '') {
              return { title: section.title, id: this.generateId(section.title, index) };
            }
            return null;
          })
          .filter(item => item !== null);
      } else {
        // None of the sections have a title: return a single TOC item for the main title.
        return [{ title: this.supportPost.attributes.title, id: this.generateId(this.supportPost.attributes.title, 0) }];
      }
    },
  },
  mounted() {
    // Ensure lenis is initialized via your mixin.
    this.$nextTick(() => {
      this.setupLenisActiveSection();
    });
  },
  beforeRouteLeave(to, from, next) {
    this.$colorMode.preference = 'light';
    next();
  },
  methods: {
    // Generate a unique ID from the section title and index.
    generateId(title, index) {
      return (
        title
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '-') +
        `-${index}`
      );
    },
    // Decide which id to assign to each content section.
    // If none of the sections have a title, we assign an id only to the first section.
    getSectionId(zone, index) {
      const sections = this.supportPost.attributes?.sections || [];
      const hasTitledSection = sections.some(
        (section) => section.title && section.title.trim() !== ''
      );
      if (hasTitledSection) {
        return zone.title ? this.generateId(zone.title, index) : '';
      } else {
        // If none have titles, only assign an id to the first section.
        return index === 0 ? this.generateId(this.supportPost.attributes.title, 0) : '';
      }
    },
    // Scroll to the section with a smooth effect.
    scrollTo(id) {
      const element = document.getElementById(id);
      if (element) {
        const offset = 100; // Adjust offset if needed.
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
    // Setup lenis scroll events to update the active section.
    setupLenisActiveSection() {
      // Query all sections that have an id.
      const sections = Array.from(document.querySelectorAll('section[id]'));
      if (!sections.length) return;

      const updateActiveSection = () => {
        const viewportCenter = window.innerHeight / 2;
        let closestSectionId = '';
        let minDistance = Infinity;

        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const sectionCenter = rect.top + rect.height / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestSectionId = section.id;
          }
        });

        if (closestSectionId) {
          this.activeSection = closestSectionId;
        }
      };

      const throttledUpdate = throttle(50, updateActiveSection);

      if (this.lenisScroll && typeof this.lenisScroll.on === 'function') {
        this.lenisScroll.on('scroll', throttledUpdate);
      } else {
        window.addEventListener('scroll', throttledUpdate);
      }
    },
  },
};
